import { addParisDays, formatParisDate, parisDayBounds, parisDayKey } from '@flux/shared';
import { useState, type JSX } from 'react';
import { Page, PageHeader } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { ApiError } from '@/lib/api/client.js';
import { ActivityRow } from './activity-row.js';
import { AlertsList } from './alerts-list.js';
import { HealthRow } from './health-row.js';
import { MaintenanceCard } from './maintenance-card.js';
import { TodoRow } from './todo-row.js';
import { useDashboardPayload } from './use-dashboard.js';

/** Panneau en liste (Santé, Alertes) : en-tête puis lignes séparées. */
function ListPanelSkeleton({
  rows,
  titleWidth,
}: {
  rows: number;
  titleWidth: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="border-b border-border px-5 pb-3 pt-4">
        <Skeleton className={`h-5 ${titleWidth}`} />
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: rows }, (_, index) => (
          <div key={`row-${String(index)}`} className="flex items-center gap-4 px-5 py-3.5">
            <Skeleton className="h-3.5 w-32 shrink-0" />
            <Skeleton className="h-3.5 w-full max-w-56" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Même forme que l'écran chargé : Activité, À traiter, Santé, Alertes. */
function DashboardSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Skeleton className="h-5 w-20" />
        <div className="grid divide-y divide-border rounded-lg border border-border bg-card sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={`activity-${String(index)}`} className="flex flex-col gap-2 px-5 py-4">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-7 w-12" />
              <Skeleton className="h-3 w-36" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Skeleton className="h-5 w-24" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <div
              key={`todo-${String(index)}`}
              className="flex flex-col gap-2 rounded-lg border border-border bg-card px-4 py-3"
            >
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-7 w-10" />
            </div>
          ))}
        </div>
      </div>

      <ListPanelSkeleton rows={5} titleWidth="w-16" />
      <ListPanelSkeleton rows={3} titleWidth="w-48" />
    </div>
  );
}

/**
 * Tableau de bord (`02` §2) : « voir en dix secondes si le flux est sain ».
 * Trois lignes globales — Activité, À traiter, Santé — plus les alertes des
 * dernières 24 h. Plus de carte par boutique : la répartition par origine vit
 * en infobulle sur les indicateurs d'activité.
 *
 * Chaque section est rendue défensivement : un payload partiel ne doit jamais
 * faire planter l'écran (`02` §0 : « jamais de page blanche »).
 */
export function DashboardPage(): JSX.Element {
  const today = parisDayKey(new Date());
  const [fromDraft, setFromDraft] = useState(today);
  const [toDraft, setToDraft] = useState(today);
  const [selectedDays, setSelectedDays] = useState({ from: today, to: today });
  const period = {
    from: parisDayBounds(selectedDays.from).start.toISOString(),
    to: parisDayBounds(selectedDays.to).end.toISOString(),
  };
  const periodLabel = selectedDays.from === selectedDays.to
    ? formatParisDate(parisDayBounds(selectedDays.from).start)
    : `${formatParisDate(parisDayBounds(selectedDays.from).start)} – ${formatParisDate(parisDayBounds(selectedDays.to).start)}`;
  const dashboardQuery = useDashboardPayload(period);
  const data = dashboardQuery.data;

  return (
    <Page>
      <PageHeader title="Tableau de bord" actions={
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">Du
            <Input type="date" aria-label="Activité du" value={fromDraft} onChange={(event) => setFromDraft(event.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">Au
            <Input type="date" aria-label="Activité au" value={toDraft} onChange={(event) => setToDraft(event.target.value)} />
          </label>
          <Button type="button" variant="outline" disabled={!fromDraft || !toDraft || fromDraft > toDraft}
            onClick={() => setSelectedDays({ from: fromDraft, to: toDraft })}>Afficher</Button>
          <Button type="button" variant="ghost" onClick={() => {
            const from = parisDayKey(addParisDays(parisDayBounds(today).start, -6));
            setFromDraft(from); setToDraft(today); setSelectedDays({ from, to: today });
          }}>7 derniers jours</Button>
        </div>
      } />

      {dashboardQuery.isPending ? <DashboardSkeleton /> : null}

      {dashboardQuery.isError ? (
        <ErrorState
          message={
            dashboardQuery.error instanceof ApiError
              ? dashboardQuery.error.message
              : 'Impossible de charger le tableau de bord.'
          }
          detail={
            dashboardQuery.error instanceof ApiError ? dashboardQuery.error.detail : undefined
          }
          onRetry={() => void dashboardQuery.refetch()}
        />
      ) : null}

      {data ? (
        <>
          {data.maintenance ? <MaintenanceCard maintenance={data.maintenance} /> : null}
          {data.activity ? <ActivityRow activity={data.activity} periodLabel={periodLabel} /> : null}
          {data.todo ? <TodoRow todo={data.todo} /> : null}
          <div className="grid gap-6 xl:grid-cols-2">
            {data.health ? <HealthRow health={data.health} /> : null}
            <AlertsList alerts={data.alerts ?? []} groups={data.alertGroups} />
          </div>
        </>
      ) : null}
    </Page>
  );
}
